/**
 * The game page's market cards (docs/contest-ux.md §2). Every card is a soft card with best
 * prices, sportsbook-style:
 *
 *   sides    a no-line book (moneyline, match winner, overtime): one price per side
 *   ladder   a spread / total: the main line, ‹ › through the alternates, prices flash in place
 *   rows     one row per player stat (a player's props), team (team totals) or outcome (3-way,
 *            futures), each row on its own main line
 *
 * Each price shows its best venue; a small "+N" inside the cell (Revision 1) opens every venue's
 * price for that side, any of which can be tapped to trade there. Below 1280px the list opens
 * inline under the card and Book / Chart toggles expand the visible line's depth ladder and price
 * history; at 1280px+ the page's insight column does both, following the last tapped or hovered
 * market/side (`ctx.inspect` / `ctx.hover`).
 *
 * Market names and line labels (a card's title, its ‹ line ›, a row's "Points O/U 24.5") are quiet
 * links to that market's page (/market, `ctx.marketLink`); prices still open the ticket.
 */
import { memo, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { LiquidityPosition, PartnerLiquidity } from "@greenbook/client";
import { sideColumnLabel, type PositionLike } from "../../lib/books.js";
import { bestQuote, type Team } from "../../lib/marketsBoard.js";
import {
  resolveRung,
  sliceOf,
  stepRung,
  venueQuotes,
  type MarketCard,
  type Row,
  type Rung,
} from "../../lib/contestMarkets.js";
import { fmtPrice, fmtSize } from "../../lib/format.js";
import { useOddsFormat } from "../../lib/odds.js";
import { SCOPES, useAccess } from "../../lib/omauth.js";
import { FlashText, Pill, PriceButton, PriceButtonSkeleton, Skeleton, SoftCard } from "../kit.js";
import { VenueLogo } from "../VenueLogo.js";
import { DepthBook } from "../DepthBook.js";
import { PriceHistoryPanel } from "../PriceHistoryPanel.js";
import { LockedInline, scopeLabel, showUnlock } from "../Gate.js";

/** Everything a card needs from the page. */
export interface CardCtx {
  contestId: string;
  /** Best prices come from these venues only (Real mode); null = every venue. */
  allowed: ReadonlySet<string> | null;
  /** Real mode: the connected venues, to mark tradeable ones in the venue list. null in Paper. */
  realVenues: ReadonlySet<string> | null;
  /** The open ticket (its price stays highlighted). */
  active: { positionHash: string; partnerId: string } | null;
  teams: { home: Team; away: Team } | null;
  onPick: (position: LiquidityPosition, pl: PartnerLiquidity) => void;
  /** ≥1280px: the insight column is on, "+N" fills it and the per-card Book/Chart toggles hide. */
  wide: boolean;
  /** Point the insight column at a market/side now (a tap). */
  inspect: (sel: InsightSel) => void;
  /** Hovering a market/side (null = left it); the page applies it after a short delay. */
  hover: (sel: InsightSel | null) => void;
  /** The side the insight column shows (its "+N" reads as selected). */
  inspectedHash: string | null;
  /** The Market page for the book these sides are on, or null when it isn't a two-sided book there. */
  marketLink: (slice: PositionLike[]) => string | null;
}

/**
 * A market name / line label as a quiet link to its Market page (hover: underline, ink). Without
 * an href it renders as plain text. `tap` gives it a 44px target on mobile (where it's the only
 * target in its spot).
 */
export function MarketLink({
  href,
  children,
  className = "",
  tap = false,
}: {
  href: string | null | undefined;
  children: ReactNode;
  className?: string;
  tap?: boolean;
}) {
  const box = tap ? "inline-flex min-h-11 max-w-full items-center lg:min-h-8" : "";
  if (!href) return <span className={`${box} ${className}`}>{children}</span>;
  return (
    <Link to={href} title="Open this market" className={`${box} underline-offset-2 hover:text-ink hover:underline ${className}`}>
      {children}
    </Link>
  );
}

/** What the insight column shows: a market's sides (by position_hash) and the side in focus. */
export interface InsightSel {
  /** "Spread · ATL −1.5", "Giannis Antetokounmpo · Points O/U 29.5". */
  title: string;
  hashes: string[];
  focus: string;
}

/** The insight selection for one side of a market slice. */
export const selFor = (title: string, slice: PositionLike[], focus: PositionLike): InsightSel => ({
  title,
  hashes: slice.map((p) => p.position_hash),
  focus: focus.position_hash,
});

/** A deep-link / chip focus for one card; `n` changes on every new focus so it re-applies. */
export interface CardFocus {
  rowKey?: string;
  rungKey?: string;
  n: number;
}

/** Futures rows shown before "Show all". */
const OUTCOMES_TOP = 8;

/* ------------------------------------------------------------------ gate */

/** Signed out → the inline sign-in lock; signed in without the scope → an Unlock button. */
export function GateNote({ scope }: { scope: string }) {
  const access = useAccess(scope);
  if (access.ok) return null;
  if (access.reason === "signed_out") return <LockedInline scope={scope} />;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 px-3 py-3 text-center text-[12px] text-faint">
      <span>Unlock {scopeLabel(scope)} to see this.</span>
      <button
        type="button"
        onClick={() => showUnlock(scope)}
        className="min-h-11 rounded-full border border-amber/60 bg-amber/15 px-3 text-[12px] font-semibold text-amber hover:bg-amber/25 lg:min-h-8"
      >
        Unlock
      </button>
    </div>
  );
}

/* ------------------------------------------------------------- side quote */

/**
 * One side's price cell: the best price and venue, with a small "+N" link inside the cell
 * ("+123 · ProphetX  +7"). Below 1280px "+N" toggles the venue list (the parent renders it
 * under the card); at 1280px+ it points the insight column here. Tapping the price trades.
 */
export function QuoteCell({
  pos,
  label,
  ctx,
  market,
  listOpen = false,
  onToggleList,
  className = "",
}: {
  pos: PositionLike;
  label?: ReactNode;
  ctx: CardCtx;
  /** The market this side belongs to (insight column title + both sides). */
  market: { title: string; slice: PositionLike[] };
  listOpen?: boolean;
  onToggleList?: () => void;
  className?: string;
}) {
  const best = bestQuote(pos, ctx.allowed);
  const total = useMemo(() => venueQuotes(pos, null).length, [pos]);
  const more = total - (best ? 1 : 0);
  const active = ctx.active?.positionHash === pos.position_hash;
  const sel = () => selFor(market.title, market.slice.length ? market.slice : [pos], pos);
  const inspected = ctx.wide && ctx.inspectedHash === pos.position_hash;
  return (
    <div
      className={`relative min-w-0 ${className}`}
      onMouseEnter={ctx.wide ? () => ctx.hover(sel()) : undefined}
      onMouseLeave={ctx.wide ? () => ctx.hover(null) : undefined}
    >
      <PriceButton
        label={label}
        price={best?.price}
        american={best?.american_odds}
        venue={best?.partner_name ?? best?.partner_id}
        active={active}
        disabled={!best}
        emptyText="Not on your venues"
        className={`h-full ${more > 0 ? "pr-10" : ""}`}
        onClick={() => {
          if (!best) return;
          if (ctx.wide) ctx.inspect(sel());
          ctx.onPick(pos as LiquidityPosition, best as PartnerLiquidity);
        }}
        title={best ? `Buy at ${best.partner_name ?? best.partner_id}` : "None of your connected venues quote this"}
      />
      {more > 0 && (
        <button
          type="button"
          onClick={() => (ctx.wide ? ctx.inspect(sel()) : onToggleList?.())}
          aria-expanded={ctx.wide ? undefined : listOpen}
          aria-pressed={ctx.wide ? inspected : undefined}
          aria-label={`${more} more venue${more === 1 ? "" : "s"}`}
          title={ctx.wide ? "Every venue's price, in the side panel" : "Every venue's price"}
          className={`nums absolute inset-y-0 right-0 z-20 flex w-10 items-center justify-center rounded-r-md text-[11px] font-medium underline-offset-2 transition-colors duration-150 hover:underline motion-reduce:transition-none ${
            listOpen || inspected ? "text-amber" : "text-dim hover:text-amber"
          }`}
        >
          {listOpen ? "✕" : `+${more}`}
        </button>
      )}
    </div>
  );
}

/** Every venue's price for one side, best first, with size; any row opens the ticket there. */
export function VenueList({ pos, label, ctx }: { pos: PositionLike; label: string; ctx: CardCtx }) {
  const { format } = useOddsFormat();
  const quotes = venueQuotes(pos, ctx.realVenues);
  return (
    <div className="gb-fade rounded-md border border-border bg-panel p-1">
      <div className="px-2 pb-1 pt-1.5 text-[11px] text-faint">
        Every venue · <span className="text-dim">{label}</span>
      </div>
      <ul>
        {quotes.map(({ pl, tradeable }, i) => {
          const active = ctx.active?.positionHash === pos.position_hash && ctx.active.partnerId === pl.partner_id;
          return (
            <li key={pl.partner_id}>
              <button
                type="button"
                onClick={() => ctx.onPick(pos as LiquidityPosition, pl as PartnerLiquidity)}
                className={`flex min-h-11 w-full items-center gap-2 rounded px-2 text-left text-[13px] hover:bg-raised lg:min-h-9 ${
                  active ? "bg-amber/10" : ""
                }`}
              >
                <VenueLogo name={pl.partner_name ?? pl.partner_id} size={18} />
                <span className={`min-w-0 flex-1 truncate ${tradeable ? "text-ink" : "text-dim"}`}>
                  {pl.partner_name ?? pl.partner_id}
                  {i === 0 && <span className="ml-1.5 text-[11px] text-up">best</span>}
                  {!tradeable && <span className="ml-1.5 text-[11px] text-faint">· not connected</span>}
                </span>
                <span className="nums shrink-0 text-[11px] text-faint" title="available size">
                  {fmtSize(pl.available)}
                </span>
                <span className={`nums w-14 shrink-0 text-right font-semibold ${active ? "text-amber" : "text-ink"}`}>
                  <FlashText value={pl.price}>{fmtPrice(pl.price, pl.american_odds, format)}</FlashText>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * A grid of sides with their venue lists. Only one list is open per grid; it opens full-width
 * under the sides so nothing squeezes at 375px.
 */
function SidesGrid({
  sides,
  labelOf,
  ctx,
  title,
  cols = 2,
  onOpen,
}: {
  sides: PositionLike[];
  labelOf: (p: PositionLike) => string;
  ctx: CardCtx;
  /** The market's name for the insight column. */
  title: string;
  cols?: 1 | 2;
  /** A venue list was opened (or "+N" tapped) on one of these sides. */
  onOpen?: () => void;
}) {
  const [openHash, setOpenHash] = useState<string | null>(null);
  const open = sides.find((s) => s.position_hash === openHash) ?? null;
  // Short labels (Over / Under / Yes / No) always sit side by side; long ones (team + line)
  // stack on a phone-width card so the name isn't truncated, and pair up once there's room.
  const short = sides.every((s) => labelOf(s).length <= 10);
  const grid = cols === 1 || sides.length < 2 ? "grid-cols-1" : short ? "grid-cols-2" : "grid-cols-1 @[22rem]:grid-cols-2";
  return (
    <div className="flex flex-col gap-2">
      <div className={`grid gap-2 ${grid}`}>
        {sides.map((s) => (
          <QuoteCell
            key={s.position_hash}
            pos={s}
            label={labelOf(s)}
            ctx={ctx}
            market={{ title, slice: sides }}
            listOpen={openHash === s.position_hash}
            onToggleList={() => {
              if (openHash !== s.position_hash) onOpen?.();
              setOpenHash((h) => (h === s.position_hash ? null : s.position_hash));
            }}
          />
        ))}
      </div>
      {open && !ctx.wide && <VenueList pos={open} label={labelOf(open)} ctx={ctx} />}
    </div>
  );
}

/* ---------------------------------------------------------------- stepper */

export function Stepper({
  index,
  count,
  label,
  onStep,
  onMain,
  atMain,
  href,
}: {
  index: number;
  count: number;
  label: string;
  onStep: (dir: -1 | 1) => void;
  onMain: () => void;
  atMain: boolean;
  /** The shown line's Market page: the label links there. */
  href?: string | null;
}) {
  const btn =
    "relative z-10 grid h-11 w-11 shrink-0 place-items-center rounded-full text-[16px] text-dim hover:bg-raised hover:text-ink disabled:opacity-30 disabled:hover:bg-transparent lg:h-8 lg:w-8";
  return (
    <div className="flex shrink-0 items-center gap-0.5">
      {!atMain && (
        <button
          type="button"
          onClick={onMain}
          className="mr-1 min-h-11 rounded-full px-2 text-[11px] text-dim hover:text-amber lg:min-h-8"
          title="Back to the main line"
        >
          Main
        </button>
      )}
      <button type="button" className={btn} onClick={() => onStep(-1)} disabled={index <= 0} aria-label="Lower line">
        ‹
      </button>
      {href ? (
        <Link
          to={href}
          className="nums inline-flex min-h-11 min-w-[3.5rem] items-center justify-center whitespace-nowrap text-[13px] font-medium text-ink underline-offset-2 hover:underline lg:min-h-8"
          title={`Open this market · ${index + 1} of ${count} lines`}
        >
          {label}
        </Link>
      ) : (
        <span className="nums min-w-[3.5rem] whitespace-nowrap text-center text-[13px] font-medium text-ink" title={`${index + 1} of ${count} lines`}>
          {label}
        </span>
      )}
      <button type="button" className={btn} onClick={() => onStep(1)} disabled={index >= count - 1} aria-label="Higher line">
        ›
      </button>
    </div>
  );
}

export function signed(n: number): string {
  const v = Math.round(n * 10) / 10;
  return v > 0 ? `+${v}` : v < 0 ? `−${Math.abs(v)}` : "0";
}

/** The stepper's line label: the total, or the home team's spread. */
/**
 * A spread side as "CLE +1.5": the team's abbreviation (the same one the card's line stepper
 * shows) instead of the full name, which doesn't fit two price buttons in a card. Over/Under and
 * anything without an abbreviation keep the usual label.
 */
function abbrSideLabel(p: PositionLike, teams: CardCtx["teams"]): string {
  const full = sideColumnLabel(p, sideKey(p));
  const k = sideKey(p).toLowerCase();
  const abbr = k === "home" ? teams?.home.abbr : k === "away" ? teams?.away.abbr : undefined;
  return abbr && p.participant_name && full.startsWith(p.participant_name) ? abbr + full.slice(p.participant_name.length) : full;
}

export function lineLabel(rung: Rung | undefined, kind: "spread" | "total", teams: CardCtx["teams"]): string {
  if (!rung || rung.line == null) return "—";
  if (kind === "total") return String(rung.line);
  const home = rung.sides.find((s) => (s.side_key ?? "").toLowerCase() === "home");
  const who = teams?.home.abbr ?? home?.participant_name ?? "Home";
  return `${who} ${signed(rung.line)}`;
}

/* ---------------------------------------------------------------- panels */

export type Panel = "book" | "chart" | null;

export function PanelToggles({ panel, onPanel }: { panel: Panel; onPanel: (p: Panel) => void }) {
  return (
    <div className="flex items-center gap-2">
      <Pill active={panel === "book"} onClick={() => onPanel(panel === "book" ? null : "book")}>
        Book
      </Pill>
      <Pill active={panel === "chart"} onClick={() => onPanel(panel === "chart" ? null : "chart")}>
        Chart
      </Pill>
    </div>
  );
}

export function PanelBody({ panel, slice, caption, ctx }: { panel: Panel; slice: PositionLike[]; caption: string; ctx: CardCtx }) {
  const depthOk = useAccess(SCOPES.depth).ok;
  const chartOk = useAccess(SCOPES.pricing).ok;
  if (!panel || slice.length === 0) return null;
  return (
    <div className="gb-fade overflow-hidden rounded-md border border-border bg-panel">
      {/* DepthBook carries its own header and side labels. */}
      {!(panel === "book" && depthOk) && (
        <div className="border-b border-border px-3 py-1.5 text-[11px] text-faint">
          {panel === "book" ? "Order book" : "Price history"} · <span className="text-dim">{caption}</span>
        </div>
      )}
      {panel === "book" ? (
        depthOk ? (
          <DepthBook positions={slice as LiquidityPosition[]} />
        ) : (
          <GateNote scope={SCOPES.depth} />
        )
      ) : chartOk ? (
        <PriceHistoryPanel contestId={ctx.contestId} positions={slice as LiquidityPosition[]} flush />
      ) : (
        <GateNote scope={SCOPES.pricing} />
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- cards */

const sideKey = (p: PositionLike) => (p.side_key ?? "").toLowerCase();

function sidesLabel(p: PositionLike, ctx: CardCtx): string {
  const s = sideKey(p);
  if (s === "home" || s === "away") {
    const t = s === "home" ? ctx.teams?.home : ctx.teams?.away;
    return p.participant_name ?? t?.name ?? t?.abbr ?? s;
  }
  return p.participant_name ?? p.title ?? s;
}

export function rowSideLabel(p: PositionLike): string {
  const s = sideKey(p);
  if (s === "over") return "Over";
  if (s === "under") return "Under";
  if (s === "yes") return "Yes";
  if (s === "no") return "No";
  return p.participant_name ?? s;
}

export const MarketCardView = memo(function MarketCardView({
  card,
  ctx,
  focus,
  highlight,
}: {
  card: MarketCard;
  ctx: CardCtx;
  focus: CardFocus | null;
  highlight: boolean;
}) {
  const [panel, setPanel] = useState<Panel>(null);
  // Ladder: the user's line (by key; falls back to the main line when it disappears).
  const [rungKey, setRungKey] = useState<string | null>(null);
  // Rows: each row's chosen line, and the row the Book/Chart panels follow.
  const [rowRungs, setRowRungs] = useState<Record<string, string>>({});
  const [activeRow, setActiveRow] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  // Apply a deep-link focus once per focus (`n`), never on stream pushes.
  useEffect(() => {
    if (!focus) return;
    if (focus.rungKey && card.kind === "ladder") setRungKey(focus.rungKey);
    if (focus.rowKey) {
      setActiveRow(focus.rowKey);
      if (focus.rungKey) setRowRungs((m) => ({ ...m, [focus.rowKey!]: focus.rungKey! }));
      if (card.kind === "rows") {
        const i = card.rows.findIndex((r) => r.key === focus.rowKey);
        if (i >= (ctx.wide ? OUTCOMES_TOP * 2 : OUTCOMES_TOP)) setShowAll(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.n]);

  const rowRung = (r: Row) => resolveRung(r.rungs, rowRungs[r.key] ?? null, r.mainIndex);
  // A long outcome list (futures) takes the full row and splits into two columns when there's room.
  const twoCol = card.kind === "rows" && card.rowKind === "outcomes" && card.rows.length > OUTCOMES_TOP;
  const top = twoCol && ctx.wide ? OUTCOMES_TOP * 2 : OUTCOMES_TOP;
  const ladderIndex = card.kind === "ladder" ? resolveRung(card.rungs, rungKey, card.mainIndex) : 0;
  const slice = sliceOf(card, ladderIndex, activeRow, rowRung);
  const sliceCaption =
    card.kind === "rows" && !(card.rowKind === "outcomes" && card.rows.length <= 3 && card.rows.length > 1)
      ? (card.rows.find((r) => r.key === activeRow) ?? card.rows[0])?.label ?? card.title
      : card.kind === "ladder"
        ? lineLabel(card.rungs[ladderIndex], card.lineKind, ctx.teams)
        : card.title;

  const href = ctx.marketLink(slice);
  const header = (right?: ReactNode) => (
    <div className="flex min-h-11 min-w-0 items-center gap-2">
      {card.kind === "rows" && card.category === "props" && (
        <VenueLogo name={card.title} logoUrl={card.imageUrl} size={28} />
      )}
      <h3 className="min-w-0 flex-1 break-words text-[14px] font-semibold leading-snug text-ink">
        <MarketLink href={href} tap>
          {card.title}
        </MarketLink>
      </h3>
      {right}
    </div>
  );

  let body: ReactNode;
  let head: ReactNode;
  if (card.kind === "sides") {
    head = header();
    body = <SidesGrid sides={card.sides} labelOf={(p) => sidesLabel(p, ctx)} ctx={ctx} title={card.title} />;
  } else if (card.kind === "ladder") {
    const rung = card.rungs[ladderIndex];
    head = header(
      card.rungs.length > 1 ? (
        <Stepper
          index={ladderIndex}
          count={card.rungs.length}
          label={lineLabel(rung, card.lineKind, ctx.teams)}
          onStep={(d) => setRungKey(card.rungs[stepRung(card.rungs.length, ladderIndex, d)].key)}
          onMain={() => setRungKey(null)}
          atMain={ladderIndex === card.mainIndex}
          href={href}
        />
      ) : undefined,
    );
    body = rung ? (
      <SidesGrid
        key={rung.key}
        sides={rung.sides}
        labelOf={(p) => abbrSideLabel(p, ctx.teams)}
        ctx={ctx}
        title={`${card.title} · ${lineLabel(rung, card.lineKind, ctx.teams)}`}
      />
    ) : null;
  } else {
    head = header();
    const rows = card.rowKind === "outcomes" && !showAll ? card.rows.slice(0, top) : card.rows;
    body = (
      <div
        className={
          twoCol
            ? "grid grid-cols-1 gap-x-6 @[60rem]:grid-cols-2 [&>*]:border-b [&>*]:border-border/70 [&>*]:!py-2"
            : "flex flex-col divide-y divide-border/70"
        }
      >
        {rows.map((r) => (
          <RowView
            key={r.key}
            row={r}
            card={card}
            ctx={ctx}
            index={rowRung(r)}
            selected={!!panel && (activeRow ?? card.rows[0]?.key) === r.key}
            onSelect={() => {
              setActiveRow(r.key);
              const rung = r.rungs[rowRung(r)];
              if (ctx.wide && rung?.sides[0]) ctx.inspect(selFor(rowTitle(card, r, rung), rung.sides, rung.sides[0]));
            }}
            onRung={(key) => setRowRungs((m) => ({ ...m, [r.key]: key }))}
            onMain={() =>
              setRowRungs((m) => {
                const next = { ...m };
                delete next[r.key];
                return next;
              })
            }
          />
        ))}
        {card.rowKind === "outcomes" && card.rows.length > top && (
          <div className="col-span-full !border-b-0 pt-2">
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="min-h-11 text-[12px] text-dim hover:text-amber lg:min-h-8"
            >
              {showAll ? "Show fewer" : `Show all ${card.rows.length}`}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-card={card.key} className={`min-w-0 scroll-mt-28 ${twoCol ? "col-span-full" : ""}`}>
      <SoftCard
        className={`@container flex flex-col gap-3 p-3 transition-shadow duration-300 motion-reduce:transition-none lg:p-4 ${
          highlight ? "ring-2 ring-amber/60" : ""
        }`}
      >
        {head}
        {body}
        {/* ≥1280px the insight column shows the book and chart instead. */}
        {!ctx.wide && (
          <>
            <div className="flex items-center justify-between gap-2 border-t border-border/70 pt-2">
              <PanelToggles panel={panel} onPanel={setPanel} />
              {card.kind === "ladder" && card.rungs.length > 1 && (
                <span className="nums text-[11px] text-faint">{card.rungs.length} lines</span>
              )}
            </div>
            <PanelBody panel={panel} slice={slice} caption={sliceCaption} ctx={ctx} />
          </>
        )}
      </SoftCard>
    </div>
  );
});

/** "Giannis Antetokounmpo · Points O/U 29.5", "2027 Champion · Boston Celtics". */
export function rowTitle(card: { title: string; rowKind: "outcomes" | "lines" }, row: Row, rung: Rung | undefined): string {
  const line = rung?.line != null ? ` ${card.rowKind === "outcomes" ? "" : "O/U "}${rung.line}` : "";
  return `${card.title} · ${row.label}${line}`;
}

function RowView({
  row,
  card,
  ctx,
  index,
  selected,
  onSelect,
  onRung,
  onMain,
}: {
  row: Row;
  card: Extract<MarketCard, { kind: "rows" }>;
  ctx: CardCtx;
  index: number;
  selected: boolean;
  onSelect: () => void;
  onRung: (key: string) => void;
  onMain: () => void;
}) {
  const rung = row.rungs[index];
  const outcome = card.rowKind === "outcomes";
  // "Points O/U 24.5"; with alternates the line moves into the stepper ("Points ‹ O/U 24.5 ›").
  const stepper = row.rungs.length > 1;
  const ou = rung?.line != null ? `${outcome ? "" : "O/U "}${rung.line}` : null;
  const label = ou && !stepper ? `${row.label} ${ou}` : row.label;
  const href = rung ? ctx.marketLink(rung.sides) : null;
  return (
    <div className={`flex flex-col gap-2 py-2 first:pt-0 last:pb-0 ${outcome ? "@md:flex-row @md:items-start" : ""}`}>
      <div className={`flex min-h-11 min-w-0 items-center gap-2 lg:min-h-8 ${outcome ? "@md:w-[38%] @md:shrink-0" : ""}`}>
        {href ? (
          <div className="min-w-0 flex-1">
            <MarketLink href={href} tap className={`break-words text-left text-[13px] leading-snug ${selected ? "text-amber" : "text-ink"}`}>
              {label}
            </MarketLink>
          </div>
        ) : (
          <button
            type="button"
            onClick={onSelect}
            className={`min-w-0 flex-1 break-words text-left text-[13px] leading-snug ${selected ? "text-amber" : "text-ink"}`}
            title="Show this row's book / chart"
          >
            {label}
          </button>
        )}
        {stepper && (
          <Stepper
            index={index}
            count={row.rungs.length}
            label={ou ?? "—"}
            onStep={(d) => onRung(row.rungs[stepRung(row.rungs.length, index, d)].key)}
            onMain={onMain}
            atMain={index === row.mainIndex}
            href={href}
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        {rung && (
          // The row's label links to its Market page, so opening a venue list is what points the
          // Book / Chart at this row.
          <SidesGrid key={rung.key} sides={rung.sides} labelOf={rowSideLabel} ctx={ctx} title={rowTitle(card, row, rung)} onOpen={onSelect} />
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- skeleton */

export function CardSkeleton({ rows = 1 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-panel-2 p-3 lg:p-4" aria-hidden>
      <div className="flex min-h-11 items-center gap-2">
        <Skeleton className="h-4 w-36 max-w-[50%]" />
        <Skeleton className="ml-auto h-6 w-24" />
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="grid grid-cols-2 gap-2">
          <PriceButtonSkeleton />
          <PriceButtonSkeleton />
        </div>
      ))}
      <div className="flex gap-2 border-t border-border/70 pt-2">
        <Skeleton className="h-7 w-14 rounded-full" />
        <Skeleton className="h-7 w-14 rounded-full" />
      </div>
    </div>
  );
}
